import { useCallback, useEffect, useRef, useState } from 'react';

const stopStream = (stream) => {
    stream?.getTracks().forEach((track) => track.stop());
};

const mapInputDevice = (device) => ({
    deviceId: device.deviceId,
    label: device.label || '',
});

const pickDefault = (devices, currentId) => {
    if (currentId && devices.some((d) => d.deviceId === currentId)) {
        return currentId;
    }
    const first = devices.find((d) => d.deviceId);
    return first?.deviceId || '';
};

export function usePreJoinMedia() {
    const [status, setStatus] = useState('loading');
    const [permissionHint, setPermissionHint] = useState('prompt');
    const [microphones, setMicrophones] = useState([]);
    const [cameras, setCameras] = useState([]);
    const [selectedMicId, setSelectedMicId] = useState('');
    const [selectedCameraId, setSelectedCameraId] = useState('');
    const [videoEnabled, setVideoEnabled] = useState(false);
    const [audioEnabled, setAudioEnabled] = useState(false);
    const [joinWithMic, setJoinWithMicState] = useState(true);
    const [joinWithCamera, setJoinWithCameraState] = useState(true);

    const previewStreamRef = useRef(null);
    const videoRef = useRef(null);
    const selectedMicRef = useRef('');
    const selectedCameraRef = useRef('');
    const joinWithMicRef = useRef(true);
    const joinWithCameraRef = useRef(true);

    const attachVideo = useCallback((stream) => {
        requestAnimationFrame(() => {
            const node = videoRef.current;
            if (!node) return;
            node.srcObject = stream;
        });
    }, []);

    const refreshDeviceLists = useCallback(async () => {
        if (!navigator.mediaDevices?.enumerateDevices) {
            return { mics: [], cams: [] };
        }
        const listed = await navigator.mediaDevices.enumerateDevices();
        const mics = listed.filter((d) => d.kind === 'audioinput').map(mapInputDevice);
        const cams = listed.filter((d) => d.kind === 'videoinput').map(mapInputDevice);
        setMicrophones(mics);
        setCameras(cams);
        return { mics, cams };
    }, []);

    const applyTrackEnabled = useCallback(() => {
        const stream = previewStreamRef.current;
        if (!stream) return;
        stream.getAudioTracks().forEach((track) => {
            track.enabled = joinWithMicRef.current;
        });
    }, []);

    const startPreview = useCallback(async (micId, cameraId) => {
        if (!navigator.mediaDevices?.getUserMedia) {
            setStatus('unsupported');
            return;
        }

        stopStream(previewStreamRef.current);
        previewStreamRef.current = null;
        setAudioEnabled(false);
        if (joinWithCameraRef.current) {
            setVideoEnabled(false);
        }

        const audio = micId ? { deviceId: { exact: micId } } : true;
        const video = cameraId ? { deviceId: { exact: cameraId } } : true;
        const attempts = joinWithCameraRef.current
            ? [
                { audio, video },
                { audio, video: false },
                { audio: false, video },
            ]
            : [
                { audio, video: false },
            ];

        for (const constraints of attempts) {
            try {
                const stream = await navigator.mediaDevices.getUserMedia(constraints);
                previewStreamRef.current = stream;
                const hasAudio = stream.getAudioTracks().length > 0;
                const hasVideo = stream.getVideoTracks().length > 0;
                setAudioEnabled(hasAudio);
                if (hasVideo) {
                    setVideoEnabled(true);
                } else if (joinWithCameraRef.current) {
                    setVideoEnabled(false);
                }
                if (!hasAudio) {
                    joinWithMicRef.current = false;
                    setJoinWithMicState(false);
                }
                if (joinWithCameraRef.current && !hasVideo) {
                    joinWithCameraRef.current = false;
                    setJoinWithCameraState(false);
                }
                applyTrackEnabled();
                attachVideo(stream);
                setStatus('ready');
                setPermissionHint('granted');
                return;
            } catch (err) {
                const denied = err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError';
                if (denied) {
                    setPermissionHint('denied');
                    setStatus('denied');
                    return;
                }
            }
        }

        setStatus('error');
    }, [applyTrackEnabled, attachVideo]);

    useEffect(() => {
        let cancelled = false;

        const init = async () => {
            setStatus('loading');
            const { mics, cams } = await refreshDeviceLists();
            if (cancelled) return;

            const micId = pickDefault(mics, '');
            const cameraId = pickDefault(cams, '');
            selectedMicRef.current = micId;
            selectedCameraRef.current = cameraId;
            setSelectedMicId(micId);
            setSelectedCameraId(cameraId);
            await startPreview(micId, cameraId);
        };

        init();

        const onDeviceChange = async () => {
            const { mics, cams } = await refreshDeviceLists();
            const micId = pickDefault(mics, selectedMicRef.current);
            const cameraId = pickDefault(cams, selectedCameraRef.current);
            selectedMicRef.current = micId;
            selectedCameraRef.current = cameraId;
            setSelectedMicId(micId);
            setSelectedCameraId(cameraId);
            await startPreview(micId, cameraId);
        };

        navigator.mediaDevices?.addEventListener?.('devicechange', onDeviceChange);

        return () => {
            cancelled = true;
            navigator.mediaDevices?.removeEventListener?.('devicechange', onDeviceChange);
            stopStream(previewStreamRef.current);
            previewStreamRef.current = null;
            if (videoRef.current) {
                videoRef.current.srcObject = null;
            }
        };
    }, [refreshDeviceLists, startPreview]);

    const selectMicrophone = useCallback(async (deviceId) => {
        selectedMicRef.current = deviceId;
        setSelectedMicId(deviceId);
        await startPreview(deviceId, selectedCameraRef.current);
    }, [startPreview]);

    const selectCamera = useCallback(async (deviceId) => {
        selectedCameraRef.current = deviceId;
        setSelectedCameraId(deviceId);
        await startPreview(selectedMicRef.current, deviceId);
    }, [startPreview]);

    const setJoinWithMic = useCallback((on) => {
        const next = Boolean(on);
        joinWithMicRef.current = next;
        setJoinWithMicState(next);
        applyTrackEnabled();
    }, [applyTrackEnabled]);

    const setJoinWithCamera = useCallback(async (on) => {
        const next = Boolean(on);
        joinWithCameraRef.current = next;
        setJoinWithCameraState(next);

        if (!next) {
            const stream = previewStreamRef.current;
            stream?.getVideoTracks().forEach((track) => {
                track.stop();
                stream.removeTrack(track);
            });
            if (videoRef.current) {
                videoRef.current.srcObject = stream || null;
            }
            return;
        }

        await startPreview(selectedMicRef.current, selectedCameraRef.current);
    }, [startPreview]);

    return {
        status,
        permissionHint,
        microphones,
        cameras,
        selectedMicId,
        selectedCameraId,
        videoEnabled,
        audioEnabled,
        joinWithMic,
        joinWithCamera,
        videoRef,
        selectMicrophone,
        selectCamera,
        setJoinWithMic,
        setJoinWithCamera,
    };
}
